import {
  ComposedConditionSet,
  ConditionSet,
  ElementType,
  ListType,
  PageType,
  QuestionAnswerObject,
  QuestionType,
  ResponseRecord,
  SubmissionInfo,
  SubmissionTypes,
  SurveyAnyPage,
  SurveyElement,
  SurveyEndPage,
  SurveyListBundleOption,
  SurveyListOption,
  SurveyPage,
  TranslationPayload,
} from '@hyper-inferno/survey-elements-core';
import {
  BaseComposedPage,
  ComposedElementBuilder,
  ComposedPageBuilder,
} from '@hyper-inferno/survey-elements-core-utils';
import {
  NavigationMethod,
  SurveySession,
} from '../survey-session/survey-session.class';
import { SurveyBundle } from '../survey-bundle/survey-bundle.class';
import { ISurveySession } from '../survey-session/survey-session.interface';
import { ListInstructionProcessor } from '../utils/constructed-list-builder';
import { getRandomizedOptions } from '../utils/list-utils';
import { UniversalPiping } from '../utils/universal-piping';

function pickTextFromCodex(
  codex: TranslationPayload,
  purpose: string,
  language: string,
) {
  const rawHtml = codex[purpose][language];
  return rawHtml;
}

export interface TransformationContextIntilialization
  extends Omit<ITransformationContext, 'listBuilding'> {
  listBuilding: {
    getOptionsByListId?: ITransformationContext['listBuilding']['getOptionsByListId'];
  };
}

// Shared logic for both Node and Angular
export interface ITransformationContext {
  bundle: SurveyBundle;
  session: SurveySession;
  handlers: Record<string, QuestionHandler<any>>;
  piping: {
    transform: (
      text: string,
      ctx: ITransformationContext,
    ) => Promise<string> | string;
  }; // Other services like Translation, etc.
  listBuilding: {
    getOptionsByListId: (
      listId: string,
      ctx: ITransformationContext,
      randomizeOptions: boolean,
    ) => SurveyListOption[];
  };
  /**
   * Persists the session data.
   * @returns A promise resolving to true if successful, false otherwise.
   */
  sessionSave: (
    sessionData: Omit<
      ISurveySession,
      'updated_at' | 'sync_status' | 'last_sync_time'
    >,
  ) => Promise<boolean> | boolean;
}

export interface QuestionHandler<T> {
  buildConfig(el: any, ctx: ITransformationContext): Promise<T> | T;

  getValue?(
    el: any,
    responseData: QuestionAnswerObject,
  ): string | number | number[] | { [key: number]: number[] };
}

export class SurveyBotEngine {
  previousPage(): any {
    const currentState = this.context.session.jsn_session_state;
    // If there's no history or we are already at the very first page
    if (
      !currentState ||
      currentState.pointer <= 0 ||
      currentState.history.length === 0
    ) {
      return null;
    }

    // 1. Calculate the target index and get the page ID
    const targetPointer = currentState.pointer - 1;
    const previousPageId = currentState.history[targetPointer];
    return previousPageId;
  }
  addOEMapping(oeMappingData: Record<string, string>) {
    this.context.session.patchOEMapping(oeMappingData);
  }
  nextPage(): SurveyAnyPage {
    const currentPage = this.context.session.current_page_id;
    if (currentPage) return this.context.bundle.fetchNextPageQuery(currentPage);
    else return this.context.bundle.fetchFirstPageQuery();
  }
  async saveState() {
    const sessionData = this.context.session.toPersistence();
    return this.context.sessionSave(sessionData);
  }

  private context: ITransformationContext;

  constructor(private Icontext: TransformationContextIntilialization) {
    // 1. Default List Building if not provided
    if (!Icontext.listBuilding.getOptionsByListId) {
      Icontext.listBuilding.getOptionsByListId =
        this.getOptionsByListId.bind(this);
    }

    this.context = Icontext as ITransformationContext;
  }

  private async handleLogicPage(
    rawPage: SurveyPage,
  ): Promise<BaseComposedPage> {
    // 1. Get all logic elements and sort them by rank (ascending)
    const logicElements = rawPage.elements
      .filter((el) => el.type === QuestionType.SKIP_LOGIC)
      .sort((a, b) => a.rank - b.rank);

    const handler = this.context.handlers[QuestionType.SKIP_LOGIC] as any;
    // as SkipLogicHandler

    if (handler) {
      // 2. Iterate through each skip logic element in order
      for (const el of logicElements) {
        const nextId = await handler.getNextPageId(
          el,
          this.context,
          this.context.handlers,
        );

        // 3. The first one to return a valid page ID "wins" the jump
        if (nextId) {
          return this.renderPage(nextId, 'skip_logic');
        }
      }
    }

    // FALLBACK: If no logic elements matched (or none exist), proceed to the next page
    const nextChronologicalPage = this.context.bundle.fetchNextPageQuery(
      rawPage.id,
    );

    if (!nextChronologicalPage) {
      return this.renderPage(this.context.bundle.getFirstEnding().id, 'end');
    }

    return this.renderPage(nextChronologicalPage.id, 'forward');
  }

  async composeElement(el: SurveyElement, page_id: number) {
    const handler = this.context.handlers[el.type];
    // Get current language from session
    const lang = this.context.session.survey_language;

    // Select the translation using the helperIO
    const question_text = el.translations?.['question_text']?.[lang]
      ? pickTextFromCodex(el.translations, 'question_text', lang)
      : el.question_text;
    // 1. Build the base element properties
    const elementBuilder = new ComposedElementBuilder({
      name: el.name,
      type: el.type,
      question_text: new UniversalPiping().transform(
        question_text,
        this.context,
      ),
      id: el.id,
      is_skippable: !el.required,
      element_type: ElementType.QUESTION_ELEMENT,
      page_id,
      rank: el.rank,
      required: el.required ?? true,
    });

    // 2. Use Handler to build the complex config
    if (handler) {
      const config = await handler.buildConfig(el, this.context);
      elementBuilder.setQuestionConfig(config);
    }
    elementBuilder.setAnswerObject(
      this.context.session.jsn_responses[String(el.id)] ?? {},
    );

    const conditonalDisplay = this.context.bundle.getConditionSetsByElementId(
      String(el.id),
    );

    if (conditonalDisplay) {
      const composedConditionSet = this.buildComposedLogic(
        conditonalDisplay,
        this.context,
      );
      elementBuilder.addConditionalDisplayMeta(composedConditionSet);
    }

    const elementPlugins = this.context.bundle.getPluginsByElementId(
      el.id,
      this.responses,
    );
    elementBuilder.setElementPlugins(elementPlugins);

    return elementBuilder.build();
  }

  private async handleElementPage(
    rawPage: SurveyPage,
    navigationMethod: NavigationMethod,
  ) {
    const elementPromises = (rawPage.elements ?? []).map(async (el) =>
      this.composeElement(el, rawPage.id),
    );

    const elements = await Promise.all(elementPromises);

    // 3. Handle Ordering Logic
    // if (rawPage.randomise) {
    // eslint-disable-next-line no-constant-condition
    if (rawPage.randomize_elements) {
      // Fisher-Yates Shuffle
      for (let i = elements.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [elements[i], elements[j]] = [elements[j], elements[i]];
      }
    } else {
      // Sort by rank (Ascending)
      elements.sort((a, b) => a.rank - b.rank);
    }

    const progress = this.context.bundle.getProgressPercentage(rawPage.id);

    this.context.session.moveToQuestion(rawPage.id, navigationMethod);
    const pagePlugins = this.context.bundle.getPluginsByPageId(
      rawPage.id,
      this.responses,
    );

    return new ComposedPageBuilder(rawPage)
      .setElements(elements)
      .setProgress(progress)
      .setPageNavigation(this.context.session.jsn_session_state)
      .setPagePlugins(pagePlugins)
      .build();
  }

  async renderPage(pageId: number, navigationMethod: NavigationMethod) {
    const rawPage = this.context.bundle.fetchPageById(pageId);

    if (!rawPage) {
      throw new Error('Page Not Found');
    }

    switch (rawPage.type) {
      case PageType.LOGIC_PAGE_MODEL:
        return this.handleLogicPage(rawPage as SurveyPage);
      case PageType.ENDING_PAGE_MODEL:
        return this.handleEndPage(rawPage as SurveyEndPage, navigationMethod);
      case PageType.ELEMENT_PAGE_MODEL:
      default:
        return this.handleElementPage(rawPage as SurveyPage, navigationMethod);
    }
  }
  handleEndPage(rawPage: SurveyEndPage, navigationMethod: NavigationMethod) {
    this.context.session.moveToQuestion(rawPage.id, navigationMethod);
    this.context.session.endSurvey(rawPage.ending_config.ending_status);
    // Check both the boolean flag and if a URL actually exists
    const isRedirecting =
      rawPage.ending_config.has_redirect &&
      rawPage.ending_config.ending_redirect_url;

    const submissionType = isRedirecting
      ? SubmissionTypes.BOT_TERMINATE_SURVEY_WITH_REDIRECT
      : SubmissionTypes.BOT_TERMINATE_SURVEY_NO_REDIRECT;

    const submissionPayload: SubmissionInfo = {
      submissionType,
      submissionPayload: {
        // Pass the URL into the payload so the UI knows where to go
        redirect_url: rawPage.ending_config.ending_redirect_url,
        sys_disposition_code: rawPage.name,
        ending_text: rawPage.ending_config.ending_text ?? 'End of Survey',
      },
      statusChange: rawPage.ending_config.ending_status,
    };

    return new ComposedPageBuilder(rawPage)
      .setPageNavigation(this.context.session.jsn_session_state)
      .setSubmissionInfo(submissionPayload)
      .build();
  }

  private buildComposedLogic(
    conditionSet: ConditionSet,
    context: ITransformationContext,
  ): ComposedConditionSet {
    const dependencies: Record<number, any> = {};

    const composedConditions = (conditionSet.conditions ?? []).map(
      (condition) => {
        const elementId = condition.condition_element_id;

        // 1. Resolve Question Type (Needed for the client to know WHICH handler to use, if any)
        const targetElement = context.bundle.fetchElementById(elementId);
        const elementType = targetElement?.type ?? 'unknown';

        if (!targetElement) {
          throw new Error('Target Element is not availabe');
        }

        // 2. Resolve the Option Key (CRITICAL: Client cannot fetch options from bundle)
        let resolvedOptionKey = 0;
        if (condition.option_id) {
          const option = context.bundle.fetchOptionById(condition.option_id);
          resolvedOptionKey = option?.option_key ?? 0;
        }

        let resolvedOptionKeys: number[] = [];
        if (condition.option_targets) {
          resolvedOptionKeys = condition.option_targets.map(
            (option_id) =>
              context.bundle.fetchOptionById(option_id)?.option_key ?? 0,
          );
        }

        // 3. Pluck user value (The dependency)
        // We use the handler's getValue to ensure the format is consistent
        // (e.g. converting numeric strings to actual numbers or handling complex types)
        const handler = this.context.handlers[elementType];
        const rawValue =
          handler && typeof handler.getValue === 'function'
            ? handler.getValue(
                targetElement,
                context.session.jsn_responses[elementId] ?? {},
              )
            : context.session.jsn_responses[elementId];

        // 4. Update the dependency map
        dependencies[elementId] = rawValue ?? null;

        return {
          ...condition,
          element_type: elementType, // Tell client what kind of question this is
          option_key: resolvedOptionKey, // Pre-resolved comparison value
          element: targetElement,
          option_keys: resolvedOptionKeys,
        };
      },
    );

    return {
      ...conditionSet,
      conditions: composedConditions,
      dependencies, // This allows the client to evaluate without a global state
    };
  }

  getFirstPage() {
    if (this.context.session.current_page_id) {
      return this.context.bundle.fetchPageById(
        this.context.session.current_page_id,
      );
    }

    return this.context.bundle.fetchFirstPageQuery();
  }

  /**
   * Retrieves and processes survey options based on a list ID.
   */
  private getOptionsByListId(
    listId: string,
    ctx: ITransformationContext,
    randomize: boolean,
    partial_data = {},
  ): SurveyListOption[] {
    const rawOptions = this.buildList(listId, ctx, randomize, partial_data);
    return this.patchOptionsWithSessionData(rawOptions);
  }

  /**
   * Fetches the list and handles randomization or dynamic instruction processing.
   */
  private buildList(
    listId: string,
    ctx: ITransformationContext,
    randomize: boolean,
    partial_data = {},
  ): SurveyListOption[] {
    const list = ctx.bundle.getListByListIdQuery(listId);

    // Guard clause: if list doesn't exist, return empty immediately
    if (!list) return [];

    if (list.list_type === ListType.NORMAL_LIST) {
      const options = list.options ?? [];
      return randomize ? getRandomizedOptions(options) : options;
    }

    if (!list?.primary_list_id) throw new Error('No parent list found');
    const enabledOptions = this.getEnabledOptions(listId, ctx, partial_data);

    // Create a Set for O(1) lookup
    const enabledKeys = new Set(enabledOptions.map((e) => e.option_key));

    const options = ctx.listBuilding
      .getOptionsByListId(list?.primary_list_id, ctx, false)
      .map((option) => ({
        ...option,
        disabled: !enabledKeys.has(option.option_key),
      }));

    return [
      {
        id: '',
        list_id: listId,
        option_key: 0,
        option_display: 'Answer Previous Questions to see Responses',
        is_open_ended: false,
        is_mutually_exclusive: false,
        is_afm_trigger: true,
        simple_props: {
          text: 'Answer Previous Question to see Responses',
          sender: this.sessionId,
          list_id: listId,
        },
        disabled: true,
        hidden: false,
      },
      ...options,
    ];
  }

  getEnabledOptions(
    listId: string,
    ctx: ITransformationContext,
    partial_data = {},
  ) {
    let options: SurveyListOption[];
    try {
      options = ListInstructionProcessor.process(listId, ctx, partial_data);
    } catch (error) {
      options = [];
    }
    return options;
  }

  /**
   * Enriches options with session data, transforming open-ended fields into piped values or Fetching the right translation.
   */
  private patchOptionsWithSessionData(
    options: SurveyListBundleOption[],
  ): SurveyListOption[] {
    const { jsn_oe_mapping } = this.context.session;

    const lang = this.context.session.survey_language;

    return options.map((option) => {
      const mappingKey = jsn_oe_mapping?.[option.id];

      // 1. Always calculate the base localized translation first
      const option_display = option.translations?.['option_display']?.[lang]
        ? pickTextFromCodex(option.translations, 'option_display', lang)
        : option.option_display;

      // 2. Check if we need to apply dynamic session data (Open-Ended mapping)
      if (option.is_open_ended && mappingKey) {
        return {
          ...option,
          is_open_ended: false,
          option_display: new UniversalPiping().transform(
            `{{${mappingKey}}}`,
            this.context,
          ),
        };
      }
      // 3. Otherwise, return the option with the option_display text
      return {
        ...option,
        option_display: option_display,
      };
    });
  }

  getGlobalPlugins(surveyId: number) {
    return this.context.bundle.getGlobalPlugins(
      surveyId,
      this.context.session.jsn_responses,
    );
  }

  public get sessionId(): string {
    return this.context.session.id;
  }

  public get surveyId(): string {
    return String(this.context.session.survey_id);
  }

  get responses(): ResponseRecord {
    return this.context.session.jsn_responses ?? {};
  }
  changeLanguage(langcode: string) {
    this.context.session.setLanguage(langcode);
  }

  getDynamicOptions(listId: string, page_data: any) {
    return this.getOptionsByListId(listId, this.context, false, page_data);
  }
}
